import React from 'react';

import { T, FONT_STACK, NUMERIC, money } from '../customer/customers.theme';
import { Spinner } from '../customer/CustomersUI';
import { COPY } from './billing.copy';
import {
  Seal,
  TickIcon,
  AlertIcon,
  ClockIcon,
  InfoIcon,
  CloseIcon,
  HandsetIcon,
  StepDots,
} from './BillingIcons';
import {
  plansFor,
  defaultPlan,
  planIdOf,
  monthsOf,
  monthlyFee,
  totalFor,
  previewNextBill,
  formatNextBill,
  normalisePhone,
  isLocked,
} from './billing.rules';
import { paySubscriptionBill, classifyPaymentError } from './billingApi';

/**
 * Paying the monthly bill.
 *
 * One card that changes its face, rather than the four separate modals the
 * phone opens and closes to do the same job:
 *
 *   form     pick a plan, type the wallet number
 *   sending  the charge is out; watch the phone and key the PIN
 *   paid     it went through, with a small receipt
 *   declined the wallet refused — the server's own Somali reason
 *   gateway  nothing was charged; safe to try again
 *   unsure   no answer came back at all; see below
 *
 * Plan and number share a screen because the plan is two taps and the number
 * is the only thing anybody actually has to think about.
 *
 * `locked` is a `stuck` business: no ✕, no Escape, no click-outside. The two
 * doors are paying and signing out, and signing out has to be there — without
 * it a locked till could not hand over to a waiter, and the shop would simply
 * stop serving.
 */

const RADIUS = 14;

export default function PaymentDialog({
  business,
  plans,
  plansError,
  plansLoading,
  onRetryPlans,
  token,
  onClose,
  onPaid,
  onSignOut,
}) {
  const locked = isLocked(business);

  const available = React.useMemo(() => plansFor(plans), [plans]);

  const [chosen, setChosen] = React.useState(null);
  const [digits, setDigits] = React.useState('');
  const [phase, setPhase] = React.useState('form');
  const [failure, setFailure] = React.useState({ kind: '', message: '' });
  const [paid, setPaid] = React.useState(null);
  const [touched, setTouched] = React.useState(false);

  /**
   * One month, chosen for them.
   *
   * Derived rather than set from an effect, which matters twice: the card
   * never paints a frame with no plan selected and a total of zero, and if
   * the plans arrive a moment after the dialog opens the default applies the
   * instant they land. It also self-corrects — a plan that is retired between
   * two sessions simply falls back to the default instead of leaving a
   * selection pointing at nothing.
   */
  const plan = React.useMemo(
    () => available.find((entry) => planIdOf(entry) === planIdOf(chosen)) || defaultPlan(available),
    [available, chosen]
  );

  /**
   * A charge that is still in flight when this unmounts must not call
   * setState on a dead component — and, more to the point, must not be
   * treated as anything at all. The till has no way to learn its outcome once
   * this card is gone.
   */
  const alive = React.useRef(true);
  React.useEffect(() => () => { alive.current = false; }, []);

  /* ── escape, only when it is allowed ──────────────────────────────── */

  const sending = phase === 'sending';

  React.useEffect(() => {
    if (locked || sending) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [locked, sending, onClose]);

  /* ── the charge ───────────────────────────────────────────────────── */

  const phone = normalisePhone(digits);
  const fee = monthlyFee(business);
  const currency = business?.currencyMark || '$';
  const total = totalFor(business, plan);

  const submit = async () => {
    if (sending) return;
    setTouched(true);

    if (!plan) return;
    if (!phone.ok) return;

    setPhase('sending');
    setFailure({ kind: '', message: '' });

    try {
      const result = await paySubscriptionBill({
        phoneNumber: phone.dial,
        subscriptionPlanId: planIdOf(plan),
        token,
      });
      if (!alive.current) return;

      setPaid({
        plan,
        total,
        dial: phone.dial,
        nextBill: formatNextBill(result.business?.nextBillDate),
      });
      setPhase('paid');

      // The server sends the updated business back, `notify` already reset to
      // `none`. Hand it up so the strip goes and nothing has to be re-fetched
      // or guessed at.
      if (result.business && onPaid) onPaid(result.business);
    } catch (error) {
      if (!alive.current) return;
      const classified = classifyPaymentError(error);
      setFailure(classified);
      setPhase(classified.kind);
    }
  };

  const backToForm = () => {
    setFailure({ kind: '', message: '' });
    setPhase('form');
  };

  /* ── chrome ───────────────────────────────────────────────────────── */

  const closable = !locked && !sending && phase !== 'paid';

  const Shell = ({ children }) => (
    <div
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && closable) onClose();
      }}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        // A wall, not a notification: the locked backdrop is darker.
        background: locked ? 'rgba(21,24,29,0.62)' : T.backdrop,
        fontFamily: FONT_STACK,
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label={COPY.title}
        style={{
          width: 460,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 48px)',
          overflowY: 'auto',
          background: T.surface,
          borderRadius: RADIUS,
          boxShadow: '0 18px 50px rgba(21,24,29,0.26)',
          boxSizing: 'border-box',
          color: T.ink,
        }}
      >
        {children}
      </div>
    </div>
  );

  const Head = ({ subtitle }) => (
    <div style={{ display: 'flex', alignItems: 'flex-start', padding: '20px 22px 0' }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: -0.2 }}>
          {COPY.title}
        </div>
        {subtitle ? (
          <div style={{ fontSize: 12.5, color: T.inkSoft, marginTop: 4 }}>{subtitle}</div>
        ) : null}
      </div>

      {closable ? (
        <button
          type="button"
          aria-label={COPY.cancel}
          onClick={onClose}
          style={{
            marginLeft: 'auto',
            flex: 'none',
            width: 28,
            height: 28,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: 'none',
            borderRadius: T.radiusPill,
            background: T.hover,
            color: T.inkSoft,
            cursor: 'pointer',
          }}
        >
          <CloseIcon />
        </button>
      ) : null}
    </div>
  );

  const Outcome = ({ tone, mark, title, body, children }) => (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '32px 26px 22px',
      }}
    >
      <Seal tone={tone}>{mark}</Seal>
      <div
        style={{
          fontSize: 19,
          fontWeight: 800,
          letterSpacing: -0.3,
          marginTop: 14,
          textAlign: 'center',
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: 13,
          lineHeight: 1.55,
          color: T.inkSoft,
          textAlign: 'center',
          marginTop: 7,
          maxWidth: 320,
        }}
      >
        {body}
      </div>
      {children}
    </div>
  );

  const Foot = ({ children, column }) => (
    <div
      style={{
        display: 'flex',
        flexDirection: column ? 'column' : 'row',
        alignItems: column ? 'stretch' : 'center',
        gap: column ? 8 : 10,
        padding: '14px 22px',
        background: T.sunk,
        borderTop: `1px solid ${T.border}`,
        borderRadius: `0 0 ${RADIUS}px ${RADIUS}px`,
      }}
    >
      {children}
    </div>
  );

  const button = (kind) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    height: kind === 'big' ? 46 : 40,
    padding: '0 18px',
    border: kind === 'ghost' ? `1px solid ${T.borderStrong}` : 'none',
    borderRadius: 9,
    background:
      kind === 'primary' || kind === 'big' ? T.brand : kind === 'ghost' ? T.surface : 'transparent',
    color: kind === 'primary' || kind === 'big' ? T.surface : T.inkSoft,
    font: 'inherit',
    fontFamily: FONT_STACK,
    fontSize: kind === 'big' ? 14 : 13,
    fontWeight: 700,
    letterSpacing: 0.3,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  });

  /* ── 1 · pick a plan, type the number ─────────────────────────────── */

  if (phase === 'form') {
    const showPhoneError = touched && !phone.ok;

    return (
      <Shell>
        <Head subtitle={business?.businessName || ''} />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 16,
            padding: '18px 22px 22px',
          }}
        >
          {/* plans */}
          {plansLoading ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 9,
                height: 58,
                color: T.inkFaint,
                fontSize: 12.5,
              }}
            >
              <Spinner />
              {COPY.loadingPlans}
            </div>
          ) : null}

          {!plansLoading && plansError ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '12px 14px',
                background: T.dangerTint,
                border: `1px solid ${T.dangerBorder}`,
                borderRadius: T.radiusControl,
                fontSize: 12.5,
                color: T.danger,
                fontWeight: 600,
              }}
            >
              {COPY.plansFailed}
              <button
                type="button"
                onClick={onRetryPlans}
                style={{
                  ...button('quiet'),
                  marginLeft: 'auto',
                  height: 28,
                  padding: '0 10px',
                  color: T.danger,
                }}
              >
                {COPY.tryAgain}
              </button>
            </div>
          ) : null}

          {!plansLoading && !plansError && available.length ? (
            <div style={{ display: 'flex', gap: 8 }}>
              {available.map((entry) => {
                const on = planIdOf(entry) === planIdOf(plan);
                return (
                  <button
                    key={planIdOf(entry)}
                    type="button"
                    onClick={() => setChosen(entry)}
                    style={{
                      flex: 1,
                      minWidth: 0,
                      padding: '9px 6px',
                      background: on ? T.selectedBg : T.surface,
                      border: `1px solid ${on ? T.brand : T.border}`,
                      boxShadow: on ? `0 0 0 1px ${T.brand} inset` : 'none',
                      borderRadius: 9,
                      font: 'inherit',
                      fontFamily: FONT_STACK,
                      cursor: 'pointer',
                      textAlign: 'center',
                    }}
                  >
                    <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: 0.4 }}>
                      {COPY.monthsChip(monthsOf(entry))}
                    </div>
                    <div
                      style={{
                        fontSize: 10.5,
                        marginTop: 2,
                        color: on ? T.brandInk : T.inkFaint,
                        ...NUMERIC,
                      }}
                    >
                      {money(totalFor(business, entry), currency)}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : null}

          {!plansLoading && !plansError && !available.length ? (
            <div style={{ fontSize: 12.5, color: T.inkSoft }}>{COPY.noPlan}</div>
          ) : null}

          {/* what it comes to */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-end',
              gap: 10,
              padding: '15px 16px',
              background: T.sunk,
              border: `1px solid ${T.border}`,
              borderRadius: T.radiusPanel,
            }}
          >
            <span
              style={{ fontSize: 30, fontWeight: 800, letterSpacing: -1, lineHeight: 1, ...NUMERIC }}
            >
              {money(total, currency)}
            </span>
            <span style={{ fontSize: 12, color: T.inkSoft, paddingBottom: 3 }}>
              {COPY.total}
            </span>

            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  textTransform: 'uppercase',
                  color: T.inkFaint,
                }}
              >
                {COPY.nextBill}
              </div>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginTop: 3, ...NUMERIC }}>
                {previewNextBill(plan)}
              </div>
            </div>
          </div>

          {/* the number */}
          <div>
            <label
              htmlFor="waafiNumber"
              style={{
                display: 'block',
                fontSize: 11.5,
                fontWeight: 700,
                color: T.inkSoft,
                marginBottom: 6,
              }}
            >
              {COPY.phoneLabel}
            </label>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                height: 46,
                overflow: 'hidden',
                background: T.surface,
                border: `1px solid ${showPhoneError ? T.brand : T.borderStrong}`,
                boxShadow: showPhoneError ? `0 0 0 3px ${T.brandTint}` : 'none',
                borderRadius: 9,
                boxSizing: 'border-box',
              }}
            >
              {/*
                Fixed, not typed. It is the only country this gateway serves,
                and it takes away the commonest mistake — the same number
                written four different ways.
              */}
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  padding: '0 12px',
                  background: T.sunk,
                  borderRight: `1px solid ${T.border}`,
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: T.inkSoft,
                  ...NUMERIC,
                }}
              >
                {COPY.countryCode}
              </span>
              <input
                id="waafiNumber"
                name="waafiNumber"
                inputMode="numeric"
                autoComplete="off"
                autoFocus
                value={digits}
                placeholder={COPY.phonePlaceholder}
                onChange={(event) => {
                  setDigits(event.target.value.replace(/[^\d\s]/g, ''));
                  setTouched(false);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    submit();
                  }
                }}
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: '100%',
                  padding: '0 13px',
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  font: 'inherit',
                  fontFamily: FONT_STACK,
                  fontSize: 15,
                  letterSpacing: 0.5,
                  color: T.ink,
                  ...NUMERIC,
                }}
              />
            </div>

            <div
              style={{
                fontSize: 11.5,
                marginTop: 6,
                color: showPhoneError ? T.brand : T.inkFaint,
                fontWeight: showPhoneError ? 600 : 400,
              }}
            >
              {showPhoneError ? COPY.phoneRequired : COPY.phoneHint}
            </div>
          </div>
        </div>

        <Foot>
          <button
            type="button"
            onClick={submit}
            disabled={!plan}
            style={{
              ...button('big'),
              flex: 1,
              opacity: plan ? 1 : 0.45,
              cursor: plan ? 'pointer' : 'not-allowed',
            }}
          >
            {COPY.payNow}
          </button>

          {/* The only other way out of a locked till. */}
          {locked ? (
            <button type="button" onClick={onSignOut} style={button('ghost')}>
              {COPY.signOut}
            </button>
          ) : null}
        </Foot>
      </Shell>
    );
  }

  /* ── 2 · the charge is out; look at the phone ─────────────────────── */

  if (phase === 'sending') {
    return (
      <Shell>
        <Head subtitle={`${money(total, currency)} · ${COPY.countryCode} ${phone.digits}`} />

        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '20px 26px 28px',
          }}
        >
          <HandsetIcon width={88} />

          <div
            style={{
              fontSize: 19,
              fontWeight: 800,
              letterSpacing: -0.3,
              marginTop: 18,
              textAlign: 'center',
            }}
          >
            {COPY.checkPhone}
          </div>
          <div
            style={{
              fontSize: 13,
              lineHeight: 1.55,
              color: T.inkSoft,
              textAlign: 'center',
              marginTop: 7,
              maxWidth: 300,
            }}
          >
            {COPY.checkPhoneSub}
          </div>

          <div style={{ marginTop: 18 }}>
            <StepDots step={2} />
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              marginTop: 16,
              padding: '9px 13px',
              background: T.pendingTint,
              border: `1px solid ${T.pendingBorder}`,
              borderRadius: T.radiusControl,
              fontSize: 11.5,
              fontWeight: 600,
              color: '#7A4E06',
            }}
          >
            <InfoIcon />
            {COPY.keepOpen}
          </div>
        </div>
      </Shell>
    );
  }

  /* ── 3 · it worked ────────────────────────────────────────────────── */

  if (phase === 'paid') {
    const months = monthsOf(paid?.plan);
    return (
      <Shell>
        <Outcome
          tone="ok"
          mark={<TickIcon size={30} />}
          title={COPY.paidTitle}
          body={paid?.nextBill ? COPY.paidSub(months, paid.nextBill) : ''}
        >
          <div
            style={{
              width: '100%',
              marginTop: 18,
              border: `1px solid ${T.border}`,
              borderRadius: T.radiusPanel,
              overflow: 'hidden',
            }}
          >
            {[
              [COPY.receiptPlan, COPY.monthsChip(months)],
              [COPY.receiptFrom, `${COPY.countryCode} ${paid?.dial?.slice(3) || ''}`],
            ].map(([key, value], index) => (
              <div
                key={key}
                style={{
                  display: 'flex',
                  padding: '10px 14px',
                  fontSize: 12.5,
                  borderTop: index ? `1px solid ${T.hairline}` : 'none',
                }}
              >
                <span style={{ color: T.inkSoft }}>{key}</span>
                <span style={{ marginLeft: 'auto', fontWeight: 700, ...NUMERIC }}>{value}</span>
              </div>
            ))}
            <div
              style={{
                display: 'flex',
                padding: '10px 14px',
                fontSize: 12.5,
                background: T.sunk,
                borderTop: `1px solid ${T.hairline}`,
              }}
            >
              <span style={{ color: T.inkSoft }}>{COPY.receiptPaid}</span>
              <span style={{ marginLeft: 'auto', fontWeight: 700, ...NUMERIC }}>
                {money(paid?.total, currency)}
              </span>
            </div>
          </div>
        </Outcome>

        <Foot>
          <button type="button" onClick={onClose} style={{ ...button('big'), flex: 1 }}>
            {COPY.ok}
          </button>
        </Foot>
      </Shell>
    );
  }

  /* ── the wallet said no ───────────────────────────────────────────── */

  if (phase === 'declined') {
    return (
      <Shell>
        <Outcome
          tone="bad"
          mark={<AlertIcon size={28} />}
          title={COPY.declinedTitle}
          body={failure.message}
        />
        <Foot>
          <button type="button" onClick={onClose} style={button('ghost')}>
            {COPY.cancel}
          </button>
          <button type="button" onClick={backToForm} style={{ ...button('primary'), flex: 1 }}>
            {COPY.retry}
          </button>
        </Foot>
      </Shell>
    );
  }

  /* ── nothing was charged ──────────────────────────────────────────── */

  if (phase === 'gateway') {
    return (
      <Shell>
        <Outcome
          tone="warn"
          mark={<ClockIcon size={28} />}
          title={COPY.gatewayTitle}
          body={failure.message}
        />
        <Foot>
          <button type="button" onClick={onClose} style={button('ghost')}>
            {COPY.cancel}
          </button>
          <button type="button" onClick={backToForm} style={{ ...button('primary'), flex: 1 }}>
            {COPY.retry}
          </button>
        </Foot>
      </Shell>
    );
  }

  /* ── nobody knows ─────────────────────────────────────────────────── */

  /**
   * The screen the phone does not have.
   *
   * Retry is the quiet, secondary action here — the opposite of the other
   * two — because the server has no idempotency key and a second charge is
   * a second charge.
   */
  return (
    <Shell>
      <Outcome
        tone="warn"
        mark={<AlertIcon size={28} />}
        title={COPY.unsureTitle}
        body={COPY.unsureBody}
      />
      {/*
        No `flex: 1` on either button here. In a column the flex main axis is
        the height, so `flex: 1` sets a basis of 0 and collapses a 46px button
        into a sliver — `align-items: stretch` already gives them full width.
      */}
      <Foot column>
        <button type="button" onClick={onClose} style={button('big')}>
          {COPY.unsureClose}
        </button>
        <button type="button" onClick={backToForm} style={{ ...button('quiet'), height: 34 }}>
          {COPY.unsureRetry}
        </button>
      </Foot>
    </Shell>
  );
}
